import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Shield, CreditCard } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const DEPOSITS = [
  { id: "standard", label: "Standard Deposit", amount: "$500", desc: "For hunts under $15,000" },
  { id: "premium", label: "Premium Deposit", amount: "$2,500", desc: "For expeditions $15,000 – $50,000" },
  { id: "elite", label: "Elite Deposit", amount: "$5,000", desc: "For expeditions $50,000+" },
];

export default function DepositPayment() {
  const [selected, setSelected] = useState("standard");
  const [expedition, setExpedition] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheckout = async () => {
    if (!email) { setError("Email is required"); return; }
    setError("");
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/api/payments/checkout`, {
        package_id: selected,
        origin_url: origin,
        expedition_name: expedition || "Expedition Deposit",
        email,
      }, { withCredentials: true });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Pay Deposit | Dispatch Hunts</title></Helmet>

      <section className="min-h-screen bg-carbon grain pt-28 pb-16" data-testid="deposit-page">
        <div className="container-dh max-w-2xl">
          <div className="text-center mb-10">
            <p className="text-label text-brass mb-3">Secure Your Spot</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-bone" data-testid="deposit-title">Pay Your Expedition Deposit</h1>
            <p className="font-body text-bone/50 mt-4">Your deposit locks your dates and confirms your booking with the outfitter.</p>
          </div>

          <div className="bg-chalk p-8 space-y-6" data-testid="deposit-form">
            {error && <div className="bg-copper/10 border border-copper/30 text-copper text-sm font-body p-3" data-testid="deposit-error">{error}</div>}

            <div>
              <label className="font-label text-xs font-semibold tracking-wider uppercase text-carbon block mb-3">Select Deposit Amount</label>
              <div className="space-y-3">
                {DEPOSITS.map((d) => (
                  <button key={d.id} onClick={() => setSelected(d.id)}
                    className={`w-full text-left p-4 border transition-all ${selected === d.id ? "border-brass bg-brass/5" : "border-carbon/10 hover:border-brass/30"}`}
                    data-testid={`deposit-option-${d.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-display text-base font-bold text-carbon">{d.label}</p>
                        <p className="font-body text-xs text-smoke">{d.desc}</p>
                      </div>
                      <p className="font-mono text-xl font-bold text-brass">{d.amount}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="font-label text-xs font-semibold tracking-wider uppercase text-carbon block mb-2">Your Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@email.com"
                className="w-full h-12 px-4 bg-bone border border-carbon/20 font-body text-sm text-carbon placeholder:text-slate/40 focus:outline-none focus:border-brass"
                data-testid="deposit-email-input" />
            </div>

            <div>
              <label className="font-label text-xs font-semibold tracking-wider uppercase text-carbon block mb-2">Expedition Name (optional)</label>
              <input type="text" value={expedition} onChange={(e) => setExpedition(e.target.value)}
                placeholder="e.g. Alaska Brown Bear — Spring 2026"
                className="w-full h-12 px-4 bg-bone border border-carbon/20 font-body text-sm text-carbon placeholder:text-slate/40 focus:outline-none focus:border-brass"
                data-testid="deposit-expedition-input" />
            </div>

            <button onClick={handleCheckout} disabled={loading} className="btn-brass w-full justify-center" data-testid="deposit-pay-btn">
              <CreditCard className="w-4 h-4" /> {loading ? "Processing..." : "Proceed to Payment"}
            </button>

            <div className="flex items-center gap-2 justify-center">
              <Shield className="w-4 h-4 text-smoke" />
              <p className="font-body text-xs text-smoke">Secured by Stripe. Your payment information is never stored on our servers.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
